import * as DialogPrimitive from '@radix-ui/react-dialog';
import {suggestedOfferingType,offeringAnalysisMediaAllowed} from '@shared/offeringClassification';
import {parseOfferInput,type OfferCandidate} from '@shared/offerInput';
import {readBrandResponse} from '@/components/brands/brand-response';
import { notifyUploadFailure } from "@/lib/system-notifications";
import { uploadErrorMessage } from '@/lib/upload-error';
import {FirstAdsCard} from '@/components/brands/FirstAdsCard';
import { BrandAnalysisStatus } from '@/components/brands/BrandAnalysisStatus';
import {WebsiteCaptureDialog} from '@/components/brands/website-capture-dialog';
import {captureAvailable, captureWebsite, saveCaptureBrand} from '@/components/brands/capture-client';
import {offeringScanContext,fillOfferingSuggestion,hasUnsavedOfferingType,productImageImportMessage} from '@/components/brands/offering-analysis-state';
import {onboardingPresentation} from '@/components/brands/onboarding-presentation';
import {PINK as ONBOARDING_ACCENT, FIELD as ONBOARDING_FIELD, LINE as ONBOARDING_LINE, INK as ONBOARDING_INK} from '../auth-theme';
import {allowsPhysicalProductStudio} from '@shared/productStudioOffering';
import {resolveOffering,type OfferingType} from '@shared/offering';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ProductVariantPicker } from "@/components/brands/ProductVariantPicker";
import type { DetectedProductVariant } from "@shared/productVariants";
import '@/components/brands/brand-content.css';
import './brand-workspace.css';
import { useChatPlatformNavigation } from './chat-platform-navigation';
import { ProductStudio } from '@/components/brands/ProductStudio';
import { IdentityEditor, BrandIdentitySummary, BrandLogoControl } from '@/components/brands/IdentityEditor';
import { legacyBrandChanges } from '@/components/brands/identity-editor-state';
import {AdIntelligencePanel} from '@/components/brands/AdIntelligencePanel';
import {fetchBrandIntelligence,activeIntelligenceStatuses} from '@/components/brands/ad-intelligence-state';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'wouter';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { triggerCreditsGate } from '@/lib/credits-gate';
import type { Brand, Product, BrandIntelligence, BrandIntelligenceAd } from '@shared/schema';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  ArrowLeft,
  Loader2,
  Tag,
  Search,
  Brain,
  RefreshCw,
  Target,
  Package,
  Lightbulb,
  Trash2,
  X,
  Upload,
  Eye,
  MessageSquare,
  TrendingUp,
  BarChart3,
  Zap,
  Link2,
  ChevronRight,
  Users,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  Palette,
} from 'lucide-react';
import { CompetitorAnalysisContent } from '@/pages/competitor-analysis';
import { useCanvasStore } from './canvas-store';
import { workspaceUrl } from './workspace-routes';
import { AdAnglesPanel } from '@/components/brands/AdAnglesPanel';
// v1.95 — Brand Identity tab moved out of Heartbeat to live on each brand.
import { BrandIdentityTab } from '@/pages/heartbeat/brand-identity';
import { ProductIdentityTab } from '@/pages/heartbeat/product-identity';

type IntelligenceData = BrandIntelligence & { ads: BrandIntelligenceAd[] };

function SkeletonCard() {
  return (
    <div
      className="rounded-2xl animate-pulse"
      style={{
        background: '#EAF0F7',
        height: 180,
        border: '1px solid #EAF0F7',
      }}
    />
  );
}

function SkeletonHeaderBar() {
  return (
    <div className="flex items-center gap-4 animate-pulse">
      <div style={{ width: 56, height: 56, borderRadius: 14, background: '#EAF0F7', flexShrink: 0 }} />
      <div className="flex-1 space-y-2">
        <div style={{ height: 20, width: 160, background: '#EAF0F7', borderRadius: 6 }} />
        <div style={{ height: 14, width: 100, background: '#EAF0F7', borderRadius: 6 }} />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status?: string | null }) {
  const colors: Record<string, { bg: string; text: string }> = {
    Active: { bg: '#D1FAE5', text: '#065F46' },
    Inactive: { bg: '#FEE2E2', text: '#991B1B' },
    Draft: { bg: '#FEF3C7', text: '#92400E' },
  };
  const s = status || 'Active';
  const c = colors[s] || { bg: '#EAF0F7', text: '#334155' };
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 20,
        background: c.bg,
        color: c.text,
        letterSpacing: '0.02em',
      }}
    >
      {s}
    </span>
  );
}

function PaletteSwatches({ colors }: { colors?: string[] | null }) {
  if (!colors || colors.length === 0) return null;
  return (
    <div className="flex gap-1">
      {colors.slice(0, 5).map((c, i) => (
        <div
          key={i}
          title={c}
          style={{
            width: 16,
            height: 16,
            borderRadius: 4,
            background: c,
            border: '1px solid rgba(0,0,0,0.08)',
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}

function ModalBackdrop({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <DialogPrimitive.Root open onOpenChange={open => { if (!open) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[9998] bg-black/45" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[9999] w-[calc(100%-32px)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl overflow-y-auto outline-none"
          style={{ maxHeight: 'calc(100dvh - 32px)', background: '#F7F9FC', border: '1px solid #EAF0F7', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div
      className="flex items-center justify-between"
      style={{ padding: '18px 24px 14px', borderBottom: '1px solid #EAF0F7' }}
    >
      <DialogPrimitive.Title style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A' }}>{title}</DialogPrimitive.Title>
      <button
        onClick={onClose}
        aria-label="Close dialog"
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: 'transparent',
          color: '#5F6D82',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: 'none',
        }}
        onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')}
        onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
      >
        <X style={{ width: 15, height: 15 }} />
      </button>
    </div>
  );
}

function FieldLabel({ children, required, htmlFor }: { children: React.ReactNode; required?: boolean; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} style={{ fontSize: 12, fontWeight: 500, color: '#526074', display: 'block', marginBottom: 5 }}>
      {children}
      {required && <span style={{ color: '#DC2626', marginLeft: 2 }}>*</span>}
    </label>
  );
}

function InputField({
  value,
  onChange,
  placeholder,
  type = 'text',
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  testId?: string;
}) {
  return (
    <input
      data-testid={testId}
      id={testId}
      aria-label={testId === 'input-canvas-brand-url' ? 'Website URL' : testId === 'input-canvas-brand-name' ? 'Brand name (optional)' : testId === 'input-canvas-brand-category' ? 'Category (optional)' : placeholder}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '8px 12px',
        borderRadius: 10,
        border: '1px solid #DCE3ED',
        background: '#F7F9FC',
        fontSize: 13.5,
        color: '#1A1A1A',
        outline: 'none',
        boxSizing: 'border-box',
      }}
      onFocus={(e) => (e.currentTarget.style.borderColor = '#94A3B8')}
      onBlur={(e) => (e.currentTarget.style.borderColor = '#DCE3ED')}
    />
  );
}

function PrimaryButton({
  onClick,
  disabled,
  loading,
  children,
  testId,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        padding: '9px 20px',
        borderRadius: 10,
        background: disabled || loading ? '#C8D3E2' : '#1A1A1A',
        color: '#FFFFFF',
        fontSize: 13.5,
        fontWeight: 600,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {loading && <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />}
      {children}
    </button>
  );
}

function GhostButton({
  onClick,
  children,
  testId,
}: {
  onClick: () => void;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      style={{
        padding: '9px 16px',
        borderRadius: 10,
        background: 'transparent',
        color: '#526074',
        fontSize: 13.5,
        fontWeight: 500,
        cursor: 'pointer',
        border: '1px solid #DCE3ED',
      }}
      onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')}
      onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
    >
      {children}
    </button>
  );
}

function DangerButton({
  onClick,
  disabled,
  loading,
  children,
  testId,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        padding: '9px 20px',
        borderRadius: 10,
        background: disabled || loading ? '#FEE2E2' : '#DC2626',
        color: '#FFFFFF',
        fontSize: 13.5,
        fontWeight: 600,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {loading && <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />}
      {children}
    </button>
  );
}

export function AnalyzeBrandModal({ onClose, onSuccess, initialUrl }: { onClose: () => void; onSuccess: (brand: Brand, reused?: boolean) => void; initialUrl?: string }) {
  const [captureUrl, setCaptureUrl] = useState<string | null>(null);
  const [url, setUrl] = useState(initialUrl ?? '');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const submitPending = useRef(false);
  const [error, setError] = useState('');
  const [urlError, setUrlError] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSubmit = async () => {
    if (submitPending.current) return;
    if (!url) { setUrlError('Website URL is required.'); return; }
    submitPending.current = true;
    setIsLoading(true);
    try {
    setUrlError('');
    setError('');
    // v7.10 — plan brand cap, checked BEFORE the paid scrape so an over-cap
    // user doesn't burn analysis credits on a brand they can't save. The
    // server enforces the same cap on POST /api/brands (this is courtesy).
    try {
      const [meRes, brandsRes] = await Promise.all([
        fetch('/api/auth/me', { credentials: 'include' }),
        fetch('/api/brands', { credentials: 'include' }),
      ]);
      if (meRes.ok && brandsRes.ok) {
        const me = await meRes.json();
        const existing = await brandsRes.json();
        const max = me?.entitlements?.maxBrands;
        if (typeof max === 'number' && Array.isArray(existing) && existing.length >= max) {
          setError(`You've reached the ${me?.entitlements?.planName ?? 'current'} plan's limit of ${max} brand${max === 1 ? '' : 's'}. Upgrade for more.`);
          return;
        }
      }
    } catch { /* fail open — the server cap still holds */ }
    try {
      const res = await fetch('/api/brand/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, name: name || undefined, category: category || undefined }),
      });
      const result = await res.json();
      if (res.status === 402 && result.code === 'CREDITS_EXHAUSTED') {
        triggerCreditsGate(Number(result.balance ?? 0), Number(result.needed ?? 0));
        setError('Not enough credits to analyze this brand.');
        return;
      }
      if (!res.ok || !result.success) {
        if (await captureAvailable(result)) { setCaptureUrl(captureWebsite(url)); return; }
        setError(result.message || 'Analysis failed. Please try again.');
        return;
      }
      const d = result.data;
      // What the user typed always wins over the analyzer's guess — a parked
      // or malformed page can return an unrelated brand_name (e.g. "Atom").
      const brandPayload = {
        name: name || d.brand_name || '',
        industry: category || d.industry_vertical || '',
        website: d.website || url || '',
        description: d.product_service_description || '',
        targetAudience: d.target_audience || '',
        toneOfVoice: d.tone_of_voice || '',
        logo: d.logo_url || null,
        colors: d.brand_colors || [],
        painPoints: d.pain_points || [],
        purchaseTriggers: d.purchase_triggers || [],
        customerObjections: d.customer_objections || [],
        desiredTransformation: d.desired_transformation || null,
        customerAvatarName: d.customer_avatar_name || null,
        awarenessLevel: d.awareness_level || null,
        brandPersonality: d.brand_personality || [],
        contentPillars: d.content_pillars || [],
        voiceWordsUsed: d.voice_words_used || [],
        voiceWordsAvoided: d.voice_words_avoided || [],
        additionalInfo: d.additional_information || '',
        competitorPositioning: d.competitor_positioning || null,
        keyStats: d.key_stats || [],
        heroTestimonials: d.hero_testimonials || [],
        pressMentions: d.press_mentions || [],
        beforeAfterResults: d.before_after_results || [],
        priceAnchoring: d.price_anchoring || null,
        hasSubscription: d.has_subscription || false,
        heroSku: d.hero_sku || null,
        offerPatterns: d.offer_patterns || [],
        photographyStyle: d.photography_style || [],
        brandEnvironments: d.brand_environments || [],
        modelDirection: d.model_direction || null,
        ageRange: d.age_range || null,
        genderSkew: d.gender_skew || null,
        geographyFocus: d.geography_focus || [],
        platformPrimary: d.platform_primary || [],
        audienceInterests: d.audience_interests || [],
        funnelStage: d.funnel_stage || null,
        funnelGoal: d.funnel_goal || null,
        headlineFont: d.fonts?.headline?.family || null,
        headlineFontWeight: d.fonts?.headline?.weight || null,
        bodyFont: d.fonts?.body?.family || null,
        bodyFontWeight: d.fonts?.body?.weight || null,
      };
      const saveRes = await fetch('/api/brands', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(brandPayload),
      });
      const saveResult = await saveRes.json();
      if (!saveRes.ok) {
        setError(saveResult.message || 'Brand analyzed but could not be saved. Please try again.');
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      // v7.50 — the server made the brand's Projects folder (v7.37 hook);
      // refetch the sidebar so it shows up NOW, not after a page refresh.
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'rootOnly'] });
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['/api/brands/onboarding/active'] });
      toast({ title: saveResult.reused ? 'Brand already added' : 'Brand analyzed', description: saveResult.reused ? 'Using your existing brand and project folder.' : 'Brand has been created from your website.' });
      onSuccess(saveResult, !!saveResult.reused);
      onClose();
    } catch {
      setError('Something went wrong. Please try again.');
    }
    } finally {
      submitPending.current = false;
      setIsLoading(false);
    }
  };

  if (captureUrl) return <WebsiteCaptureDialog website={captureUrl} initialValues={{name,industry:category}} onCancel={()=>setCaptureUrl(null)} onSave={async(result, captureRequestId, edits)=>{
    const saved = await saveCaptureBrand(result, captureRequestId, edits);
    for (const queryKey of [['/api/brands'],['/api/canvas/projects','rootOnly'],['/api/canvas/projects','all'],['/api/brands/onboarding/active']]) queryClient.invalidateQueries({queryKey});
    onSuccess(saved, !!saved.reused); onClose();
  }}/>;
  return (
    <ModalBackdrop onClose={onClose}>
      <ModalHeader title="Add your brand" onClose={onClose} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: '#526074', margin: 0 }}>Start with your website. Admoji reads your brand’s public content to prepare its colors, voice, and product context. You’ll review the details before creating your ads.</p>
        <div>
          <FieldLabel htmlFor="input-canvas-brand-url" required>Website URL</FieldLabel>
          <InputField
            value={url}
            onChange={setUrl}
            placeholder="https://yourbrand.com"
            testId="input-canvas-brand-url"
          />
          {urlError && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 4 }}>{urlError}</p>}
        </div>
        <div>
          <FieldLabel htmlFor="input-canvas-brand-name">Brand name (optional)</FieldLabel>
          <InputField value={name} onChange={setName} placeholder="Brand name" testId="input-canvas-brand-name" />
        </div>
        <div>
          <FieldLabel htmlFor="input-canvas-brand-category">Category (optional)</FieldLabel>
          <InputField value={category} onChange={setCategory} placeholder="e.g. E-commerce, SaaS" testId="input-canvas-brand-category" />
        </div>
        {error && (
          <p role="alert" style={{ fontSize: 12, color: '#DC2626', padding: '8px 12px', borderRadius: 8, background: '#FEF2F2' }}>{error}</p>
        )}
        {isLoading && <BrandAnalysisStatus />}
      </div>
      <div
        style={{ padding: '14px 24px', borderTop: '1px solid #EAF0F7', display: 'flex', justifyContent: 'flex-end', gap: 10 }}
      >
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <button className="brand-analysis-submit" type="button" onClick={handleSubmit} disabled={!url || isLoading} data-testid="button-canvas-analyze-brand">
          {isLoading ? 'Analyzing…' : 'Analyze brand'}
        </button>
      </div>
    </ModalBackdrop>
  );
}

function AddProductModal({
  brandId,
  product,
  onClose,
  onSuccess,
}: {
  brandId: number;
  product?: Product | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [productUrl, setProductUrl] = useState(product?.productUrl || '');
  const [offerCandidates,setOfferCandidates]=useState<OfferCandidate[]>([]);
  const [resolvedOfferUrl,setResolvedOfferUrl]=useState('');
  const [generateProductSheets,setGenerateProductSheets]=useState(product?.identitySettings?.generateProductSheets??true);
  const [includeVariants, setIncludeVariants] = useState(false);
  const [variants, setVariants] = useState<DetectedProductVariant[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<Set<string>>(new Set());
  const [variantsScanned, setVariantsScanned] = useState(false);
  const scanEpoch = useRef(0);
  const scanController = useRef<AbortController | null>(null);
  const savingRef = useRef(false);
  useEffect(() => () => { scanEpoch.current++; scanController.current?.abort(); }, []);
  const changeProductUrl = (url: string) => { scanEpoch.current++; scanController.current?.abort(); scanController.current = null; setIsAnalyzing(false); setOfferCandidates([]); setResolvedOfferUrl(''); setAnalyzeError(''); setMissingScannedImages(false); setProductUrl(url); if(!manualOfferingType)setOfferingType('unknown'); const priorScanPhotos=scannedPhotos.current; setPhotos(current=>current.filter(photo=>!priorScanPhotos.includes(photo))); scannedPhotos.current=[]; setVariants([]); setSelectedVariants(new Set()); setVariantsScanned(false); };
  const importingVariants = includeVariants && variants.length > 0;
  const fieldRevisions=useRef({name:0,description:0,photos:0});
  const [name, setName] = useState(product?.name || '');
  const [description, setDescription] = useState(product?.description || '');
  const [photos, setPhotos] = useState<string[]>(product?.photos || []);
  const [savedStudioPhotos, setSavedStudioPhotos] = useState<string[]>(product?.photos || []);
  // v6.17 — Product State Descriptions: free-text footnotes about the
  // product's physical states ("pouch contains single-serve packets",
  // "mixed drink is a transparent light blue"). The video engine treats
  // them as ground truth when writing scripts and running QA.
  const [stateDescriptions, setStateDescriptions] = useState<string[]>(
    Array.isArray((product as unknown as { stateDescriptions?: string[] })?.stateDescriptions)
      ? ((product as unknown as { stateDescriptions?: string[] }).stateDescriptions as string[])
      : [],
  );
  const [sizeSpecValue, setSizeSpecValue] = useState<string>(product?.sizeSpec ?? '');
  const [referenceImageRoles,setReferenceImageRoles]=useState(product?.identitySettings?.referenceImageRoles??{});
  const {data:studioBrand}=useQuery<Brand>({queryKey:['/api/brands',brandId],queryFn:async()=>{const response=await apiRequest('GET',`/api/brands/${brandId}`);return response.json();}});
  const [offeringType,setOfferingType]=useState<OfferingType>(product?.identitySettings?.offeringType??'unknown');
  const [manualOfferingType,setManualOfferingType]=useState(!!product?.identitySettings?.offeringType&&product.identitySettings.offeringType!=='unknown');
  const scannedPhotos=useRef<string[]>([]);
  const offeringTypeChanged=hasUnsavedOfferingType(product?.identitySettings?.offeringType,offeringType);
  const offeringProduct={...product,name,description,identitySettings:{...product?.identitySettings,offeringType}};
  const offering=resolveOffering(studioBrand,offeringProduct);
  const physical=!!studioBrand&&allowsPhysicalProductStudio(studioBrand,offeringProduct);
  const service=offering.type==='service';
  const software=offering.type==='software';
  const mixed=offering.type==='mixed';
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');
  const [missingScannedImages, setMissingScannedImages] = useState(false);
  const imageImportMessage = productImageImportMessage(missingScannedImages, photos.length);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const parsedInput=parseOfferInput(productUrl,studioBrand?.website);
  const analyzeInputHint = parsedInput.kind==='invalid'?parsedInput.message:'';
  const analyzeDisabled = !!analyzeInputHint || isAnalyzing || isSaving;

  const handleAnalyze = async (selectedPage?:OfferCandidate) => {
    if (scanController.current || isSaving) return;
    if (analyzeInputHint) { setAnalyzeError(analyzeInputHint); return; }
    const snapshot={name,description,photos,revisions:{...fieldRevisions.current}};
    const context=offeringScanContext(manualOfferingType?offeringType:'unknown',name,description);
    if(context&&parsedInput.kind==='keyword')context.description=[description,'Focus on: '+parsedInput.keyword].filter(Boolean).join('\n');
    const epoch = ++scanEpoch.current;
    setIsAnalyzing(true);
    setAnalyzeError(''); setMissingScannedImages(false);
    const controller = new AbortController();
    scanController.current = controller;
    let clientTimeout = setTimeout(() => controller.abort(), 70_000);
    try {
      let scanUrl=selectedPage?.url||(parsedInput.kind==='url'?parsedInput.url:'');
      if(!scanUrl&&parsedInput.kind==='keyword'){
        const discovery=await fetch(`/api/brands/${brandId}/offers/discover`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({keyword:parsedInput.keyword}),signal:controller.signal});
        const matches=await discovery.json();
        if(epoch!==scanEpoch.current)return;
        if(discovery.status===402&&matches.code==='CREDITS_EXHAUSTED'){triggerCreditsGate(Number(matches.balance??0),Number(matches.needed??0));setAnalyzeError('Not enough credits to search for this offer.');return;}
        if(!discovery.ok)throw new Error(matches.message||'Website search failed. Try a direct URL.');
        if(!matches.candidates?.length){setAnalyzeError('No matching offer found on this brand’s website. Try a more specific keyword or paste its URL.');return;}
        if(matches.candidates.length>1){setOfferCandidates(matches.candidates);return;}
        scanUrl=matches.candidates[0].url;
      }
      setOfferCandidates([]);
      clearTimeout(clientTimeout);clientTimeout=setTimeout(()=>controller.abort(),70_000);
      const res = await fetch('/api/product/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: scanUrl, includeVariants:offeringAnalysisMediaAllowed(context.type)&&includeVariants, ...(context?{offeringContext:context}:{}) }),
        signal: controller.signal,
      });
      const result = await res.json();
      if (epoch !== scanEpoch.current) return;
      if (res.status === 402 && result.code === 'CREDITS_EXHAUSTED') {
        triggerCreditsGate(Number(result.balance ?? 0), Number(result.needed ?? 0));
        setAnalyzeError('Not enough credits to analyze this product.');
        return;
      }
      if (!res.ok || !result.success) {
        setAnalyzeError(result.message || 'Analysis failed.');
        return;
      }
      // A new name/description changes the requested feature itself; do not
      // apply any suggestions returned for the previous scope.
      if(context&&(fieldRevisions.current.name!==snapshot.revisions.name||fieldRevisions.current.description!==snapshot.revisions.description))return;
      setResolvedOfferUrl(scanUrl);
      const d = result.data;
      const detectedType = suggestedOfferingType(manualOfferingType?offeringType:'unknown', d.offering_type);
      setOfferingType(detectedType);
      const allowScannedMedia = offeringAnalysisMediaAllowed(detectedType);
      const detected: DetectedProductVariant[] = allowScannedMedia && includeVariants && Array.isArray(d.variants) ? d.variants : [];
      setMissingScannedImages(allowScannedMedia && !d.photos?.length && !detected.some(v => v.imageUrl));
      setVariants(detected); setVariantsScanned(allowScannedMedia&&includeVariants);
      setSelectedVariants(new Set(detected.filter(v => v.imageUrl).map(v => v.source.variantKey)));
      {
        setName(current=>fillOfferingSuggestion(current,snapshot.name,d.product_name||'',fieldRevisions.current.name,snapshot.revisions.name));
        setDescription(current=>fillOfferingSuggestion(current,snapshot.description,d.description||'',fieldRevisions.current.description,snapshot.revisions.description));
        if(Array.isArray(d.photos)&&d.photos.length)setPhotos(current=>{const next=fillOfferingSuggestion(current,snapshot.photos,d.photos,fieldRevisions.current.photos,snapshot.revisions.photos);if(next===d.photos)scannedPhotos.current=d.photos;return next;});
        if(!allowScannedMedia){const priorScanPhotos=scannedPhotos.current;setPhotos(current=>current.filter(photo=>!priorScanPhotos.includes(photo)));scannedPhotos.current=[];}
      }
    } catch (err: unknown) {
      if (epoch !== scanEpoch.current) return;
      const isAbort = err instanceof Error && err.name === 'AbortError';
      setAnalyzeError(isAbort ? 'Timed out — please fill in the fields manually.' : err instanceof Error?err.message:'Something went wrong.');
    } finally {
      clearTimeout(clientTimeout);
      if (epoch === scanEpoch.current) { scanController.current = null; setIsAnalyzing(false); }
    }
  };

  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    fieldRevisions.current.photos++;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', body: formData });
      if (!res.ok) throw new Error(await uploadErrorMessage(res));
      const { url } = await res.json();
      fieldRevisions.current.photos++;
      setPhotos((prev) => [...prev, url]);
      setMissingScannedImages(false);
      if(software)setReferenceImageRoles(previous=>({...previous,[url]:'app-screenshot'}));
    } catch (error) {
      notifyUploadFailure({ title: 'Upload failed', description: error instanceof Error ? error.message : 'Could not upload image.' });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [toast,software]);

  const handleSave = async () => {
    if (savingRef.current || isAnalyzing || (importingVariants ? selectedVariants.size === 0 : !name)) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      if (importingVariants) {
        const response = await apiRequest('POST', `/api/brands/${brandId}/products/import-variants`, { offeringType, generateProductSheets, variants: variants.filter(v => selectedVariants.has(v.source.variantKey)) });
        const result = await response.json();
        queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'products'] });
        toast({ title: 'Variants imported', description: `${result.created} added${result.reused ? `, ${result.reused} already in your products` : ''}.` });
        onSuccess(); onClose(); return;
      }
      const savedUrl=resolvedOfferUrl||(parsedInput.kind==='url'?parsedInput.url:'');
      const stateList = stateDescriptions.map((s) => s.trim()).filter(Boolean);
      if (product) {
        await apiRequest('PATCH', `/api/products/${product.id}`, { name, description, productUrl:savedUrl, photos, generateProductSheets, offeringType, referenceImageRoles:Object.fromEntries(Object.entries(referenceImageRoles).filter(([url])=>photos.includes(url))), ...(physical?{stateDescriptions:stateList,sizeSpec:sizeSpecValue.trim()||null}:{}) });
      } else {
        await apiRequest('POST', `/api/brands/${brandId}/products`, { name, description, productUrl:savedUrl, photos, generateProductSheets, offeringType,referenceImageRoles:Object.fromEntries(Object.entries(referenceImageRoles).filter(([url])=>photos.includes(url))),...(physical?{stateDescriptions:stateList}:{}) });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'products'] });
      toast({ title: software?(product?'Platform updated':'Platform added'):service?(product?'Service updated':'Service added'):mixed?(product?'Offering updated':'Offering added'):product ? 'Product updated' : 'Product added', description: `"${name}" has been ${product ? 'updated' : 'added'}.` });
      onSuccess();
      onClose();
    } catch (error) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Could not save product.', variant: 'destructive' });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalHeader title={product ? 'Edit Offer' : 'Add Offer'} onClose={onClose} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '65vh', overflowY: 'auto' }}>
        <div>
          <FieldLabel>Offer Type</FieldLabel>
          <Select value={offeringType} disabled={isAnalyzing || isSaving || isUploading} onValueChange={value => {
            setIncludeVariants(false); setVariants([]); setSelectedVariants(new Set()); setVariantsScanned(false);
            setAnalyzeError(''); setMissingScannedImages(false); setOfferCandidates([]); setResolvedOfferUrl(''); setOfferingType(value as OfferingType); setManualOfferingType(value!=='unknown');
          }}>
            <SelectTrigger aria-label="Offer Type" className="h-10 rounded-[10px] border-[#DCE3ED] bg-white text-[13px] text-[#334155]"><SelectValue /></SelectTrigger>
            <SelectContent className="z-[10001]">
              <SelectItem value="unknown">Auto-detect / inherit brand</SelectItem>
              <SelectItem value="physical">Physical product</SelectItem>
              <SelectItem value="software">Software or digital product</SelectItem>
              <SelectItem value="service">Service</SelectItem>
              <SelectItem value="mixed">Software or services with physical products</SelectItem>
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-[#526074]">Choose what this offer sells. Auto-detect classifies this offer when you scan; otherwise it uses the brand's type. An explicit choice is preserved.</p>
        </div>
        {!product && (
          <div style={{ background: '#F7F9FC', borderRadius: 12, padding: '12px 14px', border: '1px solid #EAF0F7' }}>
            <FieldLabel>URL or keyword (auto-fill)</FieldLabel>
            <p className="mb-2 text-xs text-slate-500">Paste a page URL or enter a keyword, such as Analytics. Keywords search this brand’s website. We’ll ask you to choose if several pages match.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <InputField
                value={productUrl}
                onChange={changeProductUrl}
                placeholder="Website URL or keyword, e.g. Analytics"
                testId="input-canvas-product-url"
              />
              <button
                type="button"
                onClick={() => handleAnalyze()}
                disabled={analyzeDisabled}
                aria-describedby="app-analysis-input-hint"
                style={{
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: '1px solid #DCE3ED',
                  background: 'white',
                  fontSize: 13,
                  color: '#334155',
                  cursor: analyzeDisabled ? 'not-allowed' : 'pointer',
                  opacity: analyzeDisabled ? 0.5 : 1,
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
                data-testid="button-canvas-analyze-product"
              >
                {isAnalyzing ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : null}
                Analyze
              </button>
            </div>
            <p id="app-analysis-input-hint" role="status" className="mt-2 text-xs text-slate-500">{analyzeInputHint}</p>
            {physical&&<div className="mt-3">
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>Generate Product Sheets</span>
                <Switch aria-label="Generate Product Sheets" checked={generateProductSheets} disabled={isAnalyzing||isSaving||isUploading} onCheckedChange={setGenerateProductSheets} />
              </label>
              <p className="mt-1 text-xs text-slate-500">Turn off to use scanned or uploaded product images without generating product sheets.</p>
            </div>}
            {physical&&<label className="flex items-center justify-between gap-3 mt-3 text-sm"><span>Include variants</span><Switch aria-label="Include variants" checked={includeVariants} disabled={isAnalyzing || isSaving} onCheckedChange={checked => { scanEpoch.current++; setIncludeVariants(checked); setVariants([]); setSelectedVariants(new Set()); setVariantsScanned(false); }} /></label>}
            {offerCandidates.length>0&&<div className="mt-3 space-y-2" aria-label="Matching offer pages">
              <p className="text-sm">Choose the page to analyze:</p>
              {offerCandidates.map(candidate=><button key={candidate.url} type="button" disabled={isAnalyzing} onClick={()=>handleAnalyze(candidate)} className="block w-full rounded border p-2 text-left text-sm">
                <span className="block font-medium">{candidate.title}</span><span className="block text-xs break-all">{candidate.url}</span>
              </button>)}
            </div>}
            {imageImportMessage && <p role="status" data-testid="product-image-import-notice" className="text-sm text-amber-800 mt-2">{imageImportMessage}</p>}
            {analyzeError && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>{analyzeError}</p>}
            {isAnalyzing && <button type="button" className="mt-2 text-xs underline" onClick={() => { scanEpoch.current++; scanController.current?.abort(); scanController.current = null; setIsAnalyzing(false); setAnalyzeError(''); setMissingScannedImages(false); }}>Cancel analysis</button>}
            {isAnalyzing && <p style={{ fontSize: 12, color: '#526074', marginTop: 6 }}>Analyzing product page…</p>}
          </div>
        )}
        {importingVariants ? <ProductVariantPicker variants={variants} selected={selectedVariants} onChange={setSelectedVariants} disabled={isSaving} /> : <>
        {variantsScanned && <p role="status" className="text-sm text-slate-600">No variants with verified catalog details were found. You can add this product below.</p>}
        <div>
          <FieldLabel required>{software?'App / feature name':service?'Service name':mixed?'Offering name':'Product name'}</FieldLabel>
          {(software || service) && <p className="mb-2 text-xs text-[#526074]">Analyze fills this in from your URL or keyword. You can edit it before saving.</p>}
          <InputField value={name} onChange={value=>{fieldRevisions.current.name++;setName(value);}} placeholder={software?'App / feature name':service?'Service name':mixed?'Offering name':'Product name'} testId="input-canvas-product-name" />
        </div>
        <div>
          <FieldLabel>Description (optional)</FieldLabel>
          <textarea aria-label="Description"
            data-testid="input-canvas-product-description"
            value={description}
            onChange={(e) => {fieldRevisions.current.description++;setDescription(e.target.value);}}
            placeholder={software?'Describe this app or feature to focus the analysis':service?'Service description':mixed?'Offering description':'Product description'}
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 10,
              border: '1px solid #DCE3ED',
              background: '#F7F9FC',
              fontSize: 13.5,
              color: '#1A1A1A',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = '#94A3B8')}
            onBlur={(e) => (e.currentTarget.style.borderColor = '#DCE3ED')}
          />
        </div>
        {physical&&<div>
          <FieldLabel>Product State Descriptions (optional)</FieldLabel>
          <p style={{ fontSize: 12, color: '#526074', margin: '0 0 8px' }}>
            Footnotes about the product's different states — the AI treats these as ground truth when
            writing video scripts. E.g. "The pouch contains single-serve packets — powder only ever
            pours from a torn packet" or "The mixed drink is a transparent light blue, never opaque."
          </p>
          {stateDescriptions.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
              <textarea aria-label="Description"
                data-testid={`input-product-state-${i}`}
                value={s}
                onChange={(e) => setStateDescriptions((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))}
                placeholder="Describe one product state…"
                rows={2}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: '1px solid #DCE3ED',
                  background: '#F7F9FC',
                  fontSize: 13,
                  color: '#1A1A1A',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#94A3B8')}
                onBlur={(e) => (e.currentTarget.style.borderColor = '#DCE3ED')}
              />
              <button
                onClick={() => setStateDescriptions((prev) => prev.filter((_, j) => j !== i))}
                title="Remove"
                data-testid={`button-remove-product-state-${i}`}
                style={{
                  border: '1px solid #DCE3ED',
                  background: 'white',
                  borderRadius: 8,
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  color: '#526074',
                  fontSize: 15,
                  lineHeight: 1,
                  flexShrink: 0,
                  marginTop: 4,
                }}
              >
                ×
              </button>
            </div>
          ))}
          <button
            onClick={() => setStateDescriptions((prev) => [...prev, ''])}
            data-testid="button-add-product-state"
            style={{
              padding: '7px 12px',
              borderRadius: 10,
              border: '1px dashed #C9C4BC',
              background: 'white',
              fontSize: 12.5,
              color: '#334155',
              cursor: 'pointer',
            }}
          >
            + Add state description
          </button>
        </div>}

        {product?.id != null && offeringTypeChanged && <p role="status" className="text-sm text-slate-600">Save the offer type before using its image tools.</p>}
        {product?.id != null && studioBrand && !offeringTypeChanged && <div className="space-y-3"><ProductStudio brand={studioBrand} product={{...product,photos,identitySettings:{...product.identitySettings,version:1,referenceImageRoles}}} compact onAssetsUpdated={assets=>{if(assets.photos){setPhotos(assets.photos);setSavedStudioPhotos(assets.photos)}}} hasUnsavedPhotos={JSON.stringify(photos)!==JSON.stringify(savedStudioPhotos)} onAddSuggestedState={note=>setStateDescriptions(previous=>[...previous,note])}/>{physical&&<div><FieldLabel>Real dimensions</FieldLabel><InputField value={sizeSpecValue} onChange={setSizeSpecValue} placeholder="Real dimensions, e.g. pouch 8.5 × 5 in" testId="input-product-size-spec"/></div>}</div>}

        <div>
          <FieldLabel>{software?'App screenshots (optional)':mixed?'Offering images (optional)':physical?'Product images (optional)':'Reference images (optional)'}</FieldLabel>{software&&<p className="text-xs text-slate-500 mb-2">Upload actual app screens. Existing images remain generic references unless labeled as screenshots; mascots are brand illustrations.</p>}
          <p className="text-xs text-muted-foreground mb-2">{physical?'These images belong to this offer. Import variants to create separate offers.':'These images describe this offering; they do not create separate offerings.'}</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
            data-testid="input-canvas-product-image-upload"
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {photos.map((photo, i) => (
              <div
                key={i}
                style={{ position: 'relative', width: 64, height: 64, borderRadius: 10, overflow: 'hidden', border: '1px solid #DCE3ED', flexShrink: 0 }}
              >
                <img src={photo} alt={`${referenceImageRoles[photo]==='app-screenshot'?'App screenshot':'Reference image'} ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button
                  onClick={() => {fieldRevisions.current.photos++;setPhotos((prev) => prev.filter((_, j) => j !== i));}}
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.55)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <X style={{ width: 10, height: 10 }} />
                </button>
              </div>
            ))}
            <button
              data-testid="button-canvas-upload-product-image"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              style={{
                width: 64,
                height: 64,
                borderRadius: 10,
                border: '2px dashed #C8D3E2',
                background: '#F7F9FC',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                flexShrink: 0,
              }}
            >
              {isUploading ? (
                <Loader2 style={{ width: 16, height: 16, color: '#5F6D82' }} className="animate-spin" />
              ) : (
                <>
                  <Upload style={{ width: 16, height: 16, color: '#5F6D82' }} />
                  <span style={{ fontSize: 10, color: '#5F6D82' }}>Upload</span>
                </>
              )}
            </button>
          </div>
        </div>
        </>}
      </div>
      <div style={{ padding: '14px 24px', borderTop: '1px solid #EAF0F7', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <PrimaryButton onClick={handleSave} loading={isSaving} disabled={isAnalyzing || (importingVariants ? selectedVariants.size === 0 : !name)} testId="button-canvas-save-product">
          {importingVariants ? `Import ${selectedVariants.size} variants` : product ? 'Save changes' : 'Add Offer'}
        </PrimaryButton>
      </div>
    </ModalBackdrop>
  );
}

function EditBrandModal({ brand, onClose, onSuccess }: { brand: Brand; onClose: () => void; onSuccess: () => void }) {
  const originalBrand = useRef(brand);
  const [name, setName] = useState(brand.name || '');
  const [industry, setIndustry] = useState(brand.industry || '');
  const [website, setWebsite] = useState(brand.website || '');
  const [description, setDescription] = useState(brand.description || '');
  const [toneOfVoice, setToneOfVoice] = useState(brand.toneOfVoice || '');
  const [logo, setLogo] = useState<string | null>(brand.logo || null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleLogoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', body: formData });
      if (!res.ok) throw new Error(await uploadErrorMessage(res));
      const { url } = await res.json();
      setLogo(url);
    } catch (error) {
      notifyUploadFailure({ title: 'Upload failed', description: error instanceof Error ? error.message : 'Could not upload logo.' });
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  }, [toast]);

  const handleSave = async () => {
    if (!name) return;
    setIsSaving(true);
    try {
      const response = await apiRequest('PATCH', `/api/brands/${brand.id}`, legacyBrandChanges(originalBrand.current, { name, industry, website, description, toneOfVoice, logo }));
      const updated = await response.json();
      queryClient.setQueryData(['/api/brands', brand.id], updated);
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      toast({ title: 'Brand updated', description: `"${name}" has been updated.` });
      onSuccess();
      onClose();
    } catch {
      toast({ title: 'Error', description: 'Could not update brand.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalHeader title="Edit brand" onClose={onClose} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Logo upload */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleLogoUpload}
            data-testid="input-canvas-edit-brand-logo"
          />
          <div
            onClick={() => !isUploadingLogo && logoInputRef.current?.click()}
            data-testid="button-canvas-edit-brand-logo"
            style={{
              width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
              border: logo ? '2px solid #DCE3ED' : '2px dashed #C8D3E2',
              background: logo ? '#fff' : '#F7F9FC',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: isUploadingLogo ? 'wait' : 'pointer',
              overflow: 'hidden', position: 'relative',
            }}
          >
            {isUploadingLogo ? (
              <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #6366F1', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />
            ) : logo ? (
              <>
                <img src={logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
                <div style={{
                  position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, transition: 'opacity 0.15s',
                }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '0')}
                >
                  <Upload style={{ width: 16, height: 16, color: '#fff' }} />
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Upload style={{ width: 16, height: 16, color: '#5F6D82' }} />
                <span style={{ fontSize: 9, color: '#5F6D82', fontWeight: 500 }}>Logo</span>
              </div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <FieldLabel required>Brand name</FieldLabel>
            <InputField value={name} onChange={setName} placeholder="Brand name" testId="input-canvas-edit-brand-name" />
          </div>
        </div>
        <div>
          <FieldLabel>Industry</FieldLabel>
          <InputField value={industry} onChange={setIndustry} placeholder="e.g. E-commerce" testId="input-canvas-edit-brand-industry" />
        </div>
        <div>
          <FieldLabel>Website</FieldLabel>
          <InputField value={website} onChange={setWebsite} placeholder="https://..." testId="input-canvas-edit-brand-website" />
        </div>
        <div>
          <FieldLabel>Description</FieldLabel>
          <textarea aria-label="Description"
            data-testid="input-canvas-edit-brand-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brand description"
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 10,
              border: '1px solid #DCE3ED',
              background: '#F7F9FC',
              fontSize: 13.5,
              color: '#1A1A1A',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = '#94A3B8')}
            onBlur={(e) => (e.currentTarget.style.borderColor = '#DCE3ED')}
          />
        </div>
        <div>
          <FieldLabel>Tone of voice</FieldLabel>
          <InputField value={toneOfVoice} onChange={setToneOfVoice} placeholder="e.g. Friendly, professional" testId="input-canvas-edit-brand-tone" />
        </div>
      </div>
      <div style={{ padding: '14px 24px', borderTop: '1px solid #EAF0F7', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <PrimaryButton onClick={handleSave} loading={isSaving} disabled={!name} testId="button-canvas-save-brand">
          Save changes
        </PrimaryButton>
      </div>
    </ModalBackdrop>
  );
}

function DeleteConfirmModal({
  title,
  message,
  onClose,
  onConfirm,
  isLoading,
}: {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) {
  return (
    <ModalBackdrop onClose={onClose}>
      <ModalHeader title={title} onClose={onClose} />
      <div style={{ padding: '20px 24px' }}>
        <p style={{ fontSize: 14, color: '#526074', lineHeight: 1.6 }}>{message}</p>
      </div>
      <div style={{ padding: '14px 24px', borderTop: '1px solid #EAF0F7', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <DangerButton onClick={onConfirm} loading={isLoading} testId="button-canvas-confirm-delete">
          Delete
        </DangerButton>
      </div>
    </ModalBackdrop>
  );
}

function IntelScanModal({
  brandId,
  onClose,
}: {
  brandId: number;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSearch = async () => {
    if (!search.trim()) return;
    setIsSearching(true);
    try {
      const res = await fetch(`/api/pages/search?q=${encodeURIComponent(search.trim())}`);
      const data = await res.json();
      setResults(data.pages || []);
    } catch {
      toast({ title: 'Search failed', description: 'Could not search pages.', variant: 'destructive' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleStartScan = async (pageId: string, pageName: string) => {
    try {
      await apiRequest('POST', `/api/brands/${brandId}/intelligence/scan`, { pageId, pageName });
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'intelligence'] });
      toast({ title: 'Scan started', description: `Scanning ${pageName}'s ad library…` });
      onClose();
    } catch {
      toast({ title: 'Scan failed', description: 'Could not start the scan.', variant: 'destructive' });
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalHeader title="Scan Ad Library" onClose={onClose} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <InputField
            value={search}
            onChange={setSearch}
            placeholder="Search for a brand (e.g. Nike, Apple)"
            testId="input-canvas-intel-search"
          />
          <button
            onClick={handleSearch}
            disabled={isSearching || !search.trim()}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: 'none',
              background: '#1A1A1A',
              color: 'white',
              fontSize: 13,
              cursor: search.trim() && !isSearching ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              whiteSpace: 'nowrap',
            }}
            data-testid="button-canvas-intel-search"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          >
            {isSearching ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <Search style={{ width: 13, height: 13 }} />}
            Search
          </button>
        </div>
        {results.length > 0 && (
          <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {results.map((page: any) => (
              <button
                key={page.page_id}
                data-testid={`button-canvas-select-page-${page.page_id}`}
                onClick={() => handleStartScan(page.page_id, page.page_name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: '1px solid #EAF0F7',
                  background: '#F7F9FC',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F7F9FC')}
              >
                {page.page_logo_url ? (
                  <img src={page.page_logo_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#EAF0F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#5F6D82' }}>
                    {page.page_name?.charAt(0) || '?'}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.page_name}</p>
                  {page.category && <p style={{ fontSize: 11, color: '#5F6D82' }}>{page.category}</p>}
                </div>
                {page.ad_count > 0 && (
                  <span style={{ fontSize: 11, color: '#5F6D82', background: '#EAF0F7', padding: '2px 7px', borderRadius: 10 }}>{page.ad_count} ads</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </ModalBackdrop>
  );
}

// v7.08 — `scanning` (Dan): while the 8-phase onboarding job is still running
// the tile reads as NOT READY — content dimmed, an "Analyzing Brand" pill in
// place of the status badge — but stays fully clickable so the user can open
// it and watch the phase-by-phase bar inside.
function BrandCard({ brand, onClick, scanning }: { brand: Brand; onClick: () => void; scanning?: boolean }) {
  return (
    <article
      data-testid={`card-brand-${brand.id}`}
      data-scanning={scanning ? 'true' : undefined}
      onClick={onClick}
      className="brand-workspace-card"
    >
      <div className="brand-workspace-card-top">
        <BrandLogoControl record={brand} size={44} />
        {scanning ? (
          <span
            data-testid={`badge-brand-analyzing-${brand.id}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
              padding: '3px 9px', borderRadius: 999, background: '#F3F1EC',
              border: '1px solid #E5E1D8', color: '#756F63', fontSize: 11, fontWeight: 600,
            }}
          >
            <Loader2 style={{ width: 11, height: 11 }} className="animate-spin" />
            Analyzing Brand
          </span>
        ) : (
          <StatusBadge status={brand.status} />
        )}
      </div>
      <button type="button" className="brand-workspace-card-open" aria-label={`Open ${brand.name}`}>
        <span data-testid={`text-brand-name-${brand.id}`} className="brand-workspace-card-name">{brand.name}</span>
        <span className="brand-workspace-card-industry">{brand.industry || 'Brand workspace'}</span>
        <span className="brand-workspace-card-foot">
          <span className="brand-workspace-card-website">{brand.website || 'Brand details and products'}</span>
          <span data-testid={`link-brand-view-${brand.id}`} className="brand-workspace-card-link">
            {scanning ? 'View progress' : 'Open brand'} <ChevronRight aria-hidden="true" size={15} />
          </span>
        </span>
      </button>
      {brand.colors && brand.colors.length > 0 && <div className="brand-workspace-card-colors"><PaletteSwatches colors={brand.colors} /></div>}
    </article>
  );
}

function AddBrandCard({ onClick }: { onClick: () => void }) {
  return (
    <section
      className="brand-workspace-empty"
      data-testid="card-add-brand"
    >
      <div className="brand-workspace-empty-icon"><Tag size={23} aria-hidden="true" /></div>
      <h2>Your brands live here.</h2>
      <p>Add your first brand to keep its identity and products together while you create ads.</p>
      <button type="button" onClick={onClick}><Plus size={16} aria-hidden="true" /> Add a brand</button>
    </section>
  );
}

function TagPill({ label, color }: { label: string; color: string }) {
  return (
    <span style={{ fontSize: 11.5, padding: '3px 10px', borderRadius: 20, background: color, color: '#1A1A1A', fontWeight: 500 }}>
      {label}
    </span>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="brand-section">
      <h3 className="mb-3">{title}</h3>
      {children}
    </section>
  );
}

function StatPill({ label, value }: { label: string; value: number | string | undefined }) {
  return (
    <div style={{ flex: 1, minWidth: 0, background: '#F7F9FC', border: '1px solid #EAF0F7', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`} style={{ fontSize: 20, fontWeight: 700, color: '#1A1A1A', lineHeight: 1 }}>
        {value === undefined ? '–' : value}
      </span>
      <span style={{ fontSize: 11, fontWeight: 500, color: '#5F6D82', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    </div>
  );
}

type OnboardingPhaseEntry = {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  error?: string;
};

type OnboardingJob = {
  id: number;
  brand_id: number;
  warningDismissed?: boolean;
  // v2.03 — 'completed_with_errors' was missing here even though the server
  // emits it; the gap hid a dead status comparison (TS2367) and let the scan
  // card spin forever on warn-state jobs.
  status: 'pending' | 'running' | 'completed' | 'completed_with_errors' | 'failed';
  phases?: OnboardingPhaseEntry[];
  error_message?: string | null;
};

const ONBOARDING_PHASE_TEXT: Record<string, string> = {
  brand_dna: "Ingesting brand DNA...",
  competitor_discovery: "Discovering competitors...",
  competitor_research: "Researching competitor positioning...",
  brand_ads: "Scanning brand ad library...",
  competitor_ads: "Scanning competitor ads...",
  visual_identity: "Analyzing visual identity...",
  competitor_insights: "Extracting competitor sentiment...",
  generate_ad_angles: "Generating ad angle library...",
};

function OnboardingScanCard({ job, onRetry }: { job: OnboardingJob; onRetry: () => void }) {
  const queryClient = useQueryClient();
  const {toast} = useToast();
  const dismissWarning = useMutation({mutationFn:async()=>{
    const response=await fetch(`/api/brands/${job.brand_id}/onboarding/dismiss-warning`,{method:'POST'});
    if(!response.ok)throw new Error('Could not dismiss warning. Please try again.');
  },onSuccess:()=>{queryClient.setQueryData<OnboardingJob>(['/api/brands',job.brand_id,'onboarding'],current=>current?{...current,warningDismissed:true}:current);},onError:error=>toast({description:error.message,variant:'destructive'})});

  const isActive = job.status === 'pending' || job.status === 'running';
  const isFailed = job.status === 'failed';
  // v2.03 — completed_with_errors is a TERMINAL state (some phases failed,
  // the rest finished). It used to fall through to the default branch and
  // render a spinner + "Building Brand Intelligence…" forever — users read
  // a finished scan as permanently stuck.
  const isWarn = job.status === 'completed_with_errors';
  const {hidden,completedCount,totalPhases,progress,runningPhase,failedPhases,optionalOnlyWarning}=onboardingPresentation(job);
  if(hidden)return null;
  const currentPhaseText = runningPhase
    ? (ONBOARDING_PHASE_TEXT[runningPhase.id] || runningPhase.name)
    : "Initializing...";

  return (
    <div
      data-testid="onboarding-scan-card"
      style={{
        position: 'relative',
        borderRadius: 14,
        border: `1px solid ${isFailed ? '#FECACA' : isWarn ? '#FDE68A' : ONBOARDING_LINE}`,
        background: isFailed ? '#FFF5F5' : isWarn ? '#FFFBEB' : ONBOARDING_FIELD,
        padding: isWarn ? '14px 40px 14px 16px' : '14px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      {isWarn && <button type="button" aria-label="Dismiss scan warning permanently" title="Dismiss this warning permanently for this brand" disabled={dismissWarning.isPending} onClick={()=>dismissWarning.mutate()} className="absolute right-2 top-2 rounded p-1 text-amber-800 hover:bg-amber-100 disabled:opacity-50"><X className="h-4 w-4"/></button>}
      <div style={{ paddingTop: 2 }}>
        {isFailed ? (
          <AlertCircle style={{ width: 18, height: 18, color: '#DC2626', flexShrink: 0 }} />
        ) : isWarn ? (
          <AlertCircle style={{ width: 18, height: 18, color: '#D97706', flexShrink: 0 }} />
        ) : (
          <Loader2 style={{ width: 18, height: 18, color: ONBOARDING_ACCENT, flexShrink: 0 }} className="animate-spin" />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: isFailed ? '#991B1B' : isWarn ? '#92400E' : ONBOARDING_INK, margin: 0 }}>
            {isFailed ? 'Intelligence Scan Failed' : isWarn ? (optionalOnlyWarning ? 'Brand setup complete — optional research unavailable' : `Scan finished — ${completedCount}/${totalPhases} steps OK`) : 'Building Brand Intelligence…'}
          </p>
          {isActive && (
            <span style={{ fontSize: 12, fontWeight: 600, color: ONBOARDING_ACCENT }}>{progress}%</span>
          )}
        </div>
        {isActive && (
          <>
            <div style={{
              width: '100%',
              height: 5,
              background: ONBOARDING_LINE,
              borderRadius: 3,
              overflow: 'hidden',
              marginTop: 6,
              marginBottom: 6,
            }}>
              <div style={{
                height: '100%',
                width: `${Math.max(progress, 3)}%`,
                background: ONBOARDING_ACCENT,
                borderRadius: 3,
                transition: 'width 0.7s ease-out',
              }} />
            </div>
            <p style={{ fontSize: 12, color: ONBOARDING_ACCENT, margin: 0 }}>
              {currentPhaseText}
            </p>
          </>
        )}
        {isFailed && job.error_message && (
          <p style={{ fontSize: 11.5, color: '#B91C1C', marginTop: 2 }}>{job.error_message}</p>
        )}
        {isWarn && (
          <div style={{ marginTop: 4 }}>
            {optionalOnlyWarning && <p style={{fontSize:12,margin:0}}>Existing ads are not required. You can create ads once your product or offer is ready. Retrying this research is optional.</p>}
            {failedPhases.length === 0 ? (
              <p style={{ fontSize: 11.5, color: '#92400E', margin: 0 }}>Some steps reported warnings — everything else is ready to use.</p>
            ) : (
              failedPhases.map((p) => (
                <p key={p.id} style={{ fontSize: 11.5, color: '#92400E', margin: '2px 0 0' }}>
                  {`${p.name || p.id}: ${p.error || 'did not complete'}`}
                </p>
              ))
            )}
          </div>
        )}
      </div>
      {isActive && (
        <span style={{ fontSize: 12, color: ONBOARDING_ACCENT, fontWeight: 500, flexShrink: 0, paddingTop: 2 }}>
          {completedCount}/{totalPhases}
        </span>
      )}
      {isFailed && (
        <button
          data-testid="button-retry-onboarding"
          onClick={onRetry}
          style={{
            padding: '6px 14px',
            borderRadius: 10,
            background: '#DC2626',
            color: 'white',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            border: 'none',
            flexShrink: 0,
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

export function OverviewTab({ brand, brandId, adAccountId, refreshSignal = 0, startWithProduct = false, onProductSetupOpened }: { brand: Brand; brandId: number; adAccountId?: number; refreshSignal?: number; startWithProduct?: boolean; onProductSetupOpened?: () => void }) {
  const [, navigate] = useLocation();
  const [showAddProduct, setShowAddProduct] = useState(startWithProduct);
  useEffect(() => { if (startWithProduct) { setShowAddProduct(true); onProductSetupOpened?.(); } }, [startWithProduct, onProductSetupOpened]);
  const [productRefresh, setProductRefresh] = useState(0);
  const [reviewProductId, setReviewProductId] = useState<number | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: onboardingJob } = useQuery<OnboardingJob | null>({
    queryKey: ['/api/brands', brandId, 'onboarding'],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/onboarding`);
      if (!res.ok) return null;
      return res.json();
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === 'pending' || data.status === 'running')) return 3000;
      return false;
    },
    enabled: !!brandId,
  });

  // Refresh stats when onboarding finishes
  const onboardingStatus = onboardingJob?.status;
  useEffect(() => {
    if (onboardingStatus === 'completed' || onboardingStatus === 'completed_with_errors') {
      queryClient.invalidateQueries({ queryKey: ['/api/heartbeat/brands', brandId, 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId] });
    }
  }, [onboardingStatus, brandId, queryClient]);

  const retryOnboarding = async () => {
    try {
      await apiRequest('POST', `/api/brands/${brandId}/onboarding/start`);
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'onboarding'] });
      toast({ title: 'Scan restarted', description: 'Building brand intelligence…' });
    } catch {
      toast({ title: 'Failed to restart scan', variant: 'destructive' });
    }
  };

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['/api/brands', brandId, 'products'],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/products`);
      return res.json();
    },
  });

  const { data: firstAdsSetup } = useQuery<{ visible: boolean; ready: boolean } | null>({
    queryKey: ['/api/brands', brandId, 'first-ads'],
    queryFn: async () => {
      const response = await fetch(`/api/brands/${brandId}/first-ads`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Could not check setup progress.');
      return response.json();
    },
  });
  const brandFolders = useQuery<Array<{ id: number; brandId?: number; brand_id?: number; isFolder?: boolean; is_folder?: boolean }>>({
    queryKey: ['/api/canvas/projects', 'rootOnly'],
    queryFn: async () => {
      const response = await fetch('/api/canvas/projects?rootOnly=1');
      if (!response.ok) throw new Error('Could not load your brand workspace.');
      return response.json();
    },
    staleTime: 30000,
  });

  const openBrandTask = () => {
    const folder = brandFolders.data?.find(item => (item.isFolder ?? item.is_folder) && Number(item.brandId ?? item.brand_id) === brandId);
    if (!folder) {
      void brandFolders.refetch();
      toast({ description: 'Your brand workspace is still loading. Please try again.', variant: 'destructive' });
      return;
    }
    const store = useCanvasStore.getState();
    store.setFolderProjectId(folder.id);
    store.addOpenFolder(folder.id);
    useCanvasStore.setState({ activeProjectId: null, activeTaskId: null, activeBrandId: brandId, activeAdAccountId: null, pendingFirstAdBrandId: brandId });
    store.setActiveView('chat-only');
    navigate(workspaceUrl('chat-only', null, folder.id));
  };

  // Real angle count: copy-set angles + legacy brand-level angles. The old
  // stat read the DEPRECATED saved_angles table and showed 0 forever once
  // angles moved to ad_angles/copy sets. Query key matches AdAnglesPanel's
  // list so the two share a cache entry.
  const { data: angleSetsData } = useQuery<{ sets: Array<{ angleCount: number | null }>; legacyCount: number }>({
    queryKey: ['angle-sets', brandId],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/angle-sets`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load copy sets');
      return res.json();
    },
  });
  const totalAngles =
    (angleSetsData?.sets ?? []).reduce((sum, s) => sum + (s.angleCount ?? 0), 0) +
    (angleSetsData?.legacyCount ?? 0);

  const { data: heartbeatStats } = useQuery<{ totalChunks: number }>({
    queryKey: ['/api/heartbeat/brands', brandId, 'stats'],
    queryFn: async () => {
      const res = await fetch(`/api/heartbeat/brands/${brandId}/stats`);
      return res.json();
    },
    enabled: !!brandId,
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest('DELETE', `/api/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'products'] });
      toast({ title: 'Product deleted' });
      setDeletingProductId(null);
      setProductRefresh(value => value + 1);
    },
    onError: () => toast({ title: 'Could not delete product', variant: 'destructive' }),
  });

  return (
    <div className="brand-workspace-overview">
      <FirstAdsCard brandId={brandId} onAddProduct={() => setShowAddProduct(true)} onReviewProduct={setReviewProductId} />
      {/* Onboarding scan status card */}
      {onboardingJob && onboardingJob.status !== 'completed' && (
        <OnboardingScanCard job={onboardingJob} onRetry={retryOnboarding} />
      )}
      {/* Re-scan button when scan is already complete */}
      {onboardingJob && onboardingJob.status === 'completed' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            data-testid="button-rescan-brand"
            onClick={retryOnboarding}
            style={{
              border: '1px solid #DCE3ED',
              borderRadius: 7,
              padding: '5px 12px',
              background: 'white',
              fontSize: 12,
              color: '#5F6D82',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <RefreshCw style={{ width: 11, height: 11 }} />
            Re-scan brand
          </button>
        </div>
      )}
      {firstAdsSetup?.visible === false && firstAdsSetup.ready && (
        <div className="brand-workspace-next-ad">
          <div>
            <strong>Ready to make another ad?</strong>
            <p>Create with {brand.name} and its saved product details.</p>
          </div>
          <button type="button" onClick={openBrandTask} disabled={brandFolders.isPending}>Create an ad <ChevronRight size={15} aria-hidden="true" /></button>
        </div>
      )}
      <ProductIdentityTab
        brandId={brandId}
        requestedProductId={reviewProductId}
        onProductOpened={() => setReviewProductId(null)}
        embedded
        refreshSignal={productRefresh + refreshSignal}
        onAddProduct={() => setShowAddProduct(true)}
        onEditProduct={product => setEditingProduct(product as unknown as Product)}
        onDeleteProduct={product => setDeletingProductId(product.id)}
      />
      <BrandIdentitySummary key={brandId} record={brand} overview><IdentityEditor record={brand} visual={false} onUpdated={updated => queryClient.setQueryData(['/api/brands', brandId], updated)} /></BrandIdentitySummary>
      <div className="brand-workspace-stats" aria-label="Brand activity">
        <StatPill label="Ad Angles" value={totalAngles} />
        <StatPill label="Products" value={products.length} />
        <StatPill label="DNA Nodes" value={heartbeatStats?.totalChunks} />
      </div>

      {showAddProduct && (
        <AddProductModal brandId={brandId} onClose={() => setShowAddProduct(false)} onSuccess={() => { setShowAddProduct(false); setProductRefresh(value => value + 1); }} />
      )}
      {editingProduct && (
        <AddProductModal brandId={brandId} product={editingProduct} onClose={() => setEditingProduct(null)} onSuccess={() => { setEditingProduct(null); setProductRefresh(value => value + 1); }} />
      )}
      {deletingProductId !== null && (
        <DeleteConfirmModal
          title="Delete product"
          message="Are you sure you want to delete this product? This cannot be undone."
          onClose={() => setDeletingProductId(null)}
          onConfirm={() => deleteProductMutation.mutate(deletingProductId!)}
          isLoading={deleteProductMutation.isPending}
        />
      )}
    </div>
  );
}

function AdAnglesTab({ brandId }: { brandId: number }) {
  return <section className="brand-section"><AdAnglesPanel brandId={brandId} /></section>;
}

function AdIntelligenceTab({ brandId }: { brandId: number }) {
  const [showScanModal,setShowScanModal]=useState(false);
  const query=useQuery<IntelligenceData|null>({
    queryKey:['/api/brands',brandId,'intelligence'],
    queryFn:()=>fetchBrandIntelligence(brandId),
    refetchInterval:query=>query.state.data&&activeIntelligenceStatuses.has(query.state.data.status)?3000:false,
  });
  return <><AdIntelligencePanel data={query.data} loading={query.isLoading} error={query.error} onRefresh={()=>void query.refetch()} onScan={()=>setShowScanModal(true)}/>
    {showScanModal&&<IntelScanModal brandId={brandId} onClose={()=>setShowScanModal(false)}/>}
  </>;
}

function DetailSkeleton() {
  const shimmer = { background: 'linear-gradient(90deg, #EAF0F7 25%, #EAF0F7 50%, #EAF0F7 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }`}</style>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, ...shimmer }} />
        <div style={{ width: 52, height: 52, borderRadius: 14, ...shimmer }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ width: '40%', height: 18, borderRadius: 6, ...shimmer }} />
          <div style={{ width: '25%', height: 13, borderRadius: 6, ...shimmer }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {[1, 2, 3].map((i) => <div key={i} style={{ flex: 1, height: 64, borderRadius: 12, ...shimmer }} />)}
      </div>
      <div style={{ height: 200, borderRadius: 14, ...shimmer }} />
    </div>
  );
}

function BrandDetailView({ brandId, initialBrand, onBack, requestedProductId, requestedCompetitorId, startWithProduct = false }: { brandId: number; initialBrand: Brand; onBack: () => void; requestedProductId?: number; requestedCompetitorId?: number; startWithProduct?: boolean }) {
  const [productSetupPending, setProductSetupPending] = useState(startWithProduct);
  // v3.91 — honor a one-shot deep-link tab (welcome "Ad Angles" shortcut sets
  // pendingBrandsTab='angles' before opening this view), then clear it.
  const [tab, setTab] = useState<'overview' | 'angles' | 'competitors' | 'intelligence' | 'identity'>(() => {
    const pending = useCanvasStore.getState().pendingBrandsTab;
    if (pending) useCanvasStore.setState({ pendingBrandsTab: null });
    if (startWithProduct) return 'overview';
    if (pending) return pending === 'products' ? 'overview' : pending;
    return 'overview';
  });
  useEffect(() => { if (requestedCompetitorId != null) setTab('competitors'); }, [requestedCompetitorId]);
  const { data: requestedProducts, isError: productLinkFailed } = useQuery<Product[]>({
    queryKey: ['/api/brands', brandId, 'products'],
    queryFn: async () => {
      const response = await fetch(`/api/brands/${brandId}/products`, { credentials: 'include' });
      if (!response.ok) throw new Error('Could not load products');
      return response.json();
    },
    enabled: requestedProductId != null,
  });
  const [productsEditTarget, setProductsEditTarget] = useState<Product | null>(null);
  const [productsRefresh, setProductsRefresh] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [visualIdentityStatus, setVisualIdentityStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  useEffect(() => {
    if (requestedProductId == null) return;
    setTab('overview');
    if (!requestedProducts && !productLinkFailed) return;
    const product = requestedProducts?.find(item => item.id === requestedProductId);
    if (product) setProductsEditTarget(product);
    else toast({ title: 'Product unavailable', description: 'This product could not be opened. Select a product from this brand.', variant: 'destructive' });
    useChatPlatformNavigation.setState({ target: null });
  }, [requestedProductId, requestedProducts, productLinkFailed, toast]);

  const { data: brand, isLoading: isBrandLoading } = useQuery<Brand | null>({
    queryKey: ['/api/brands', brandId],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}`);
      return readBrandResponse(res);
    },
    initialData: initialBrand,
    staleTime: 30_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  useEffect(() => {
    if (brand !== null) return;
    void queryClient.invalidateQueries({queryKey: ['/api/brands'], exact: true});
    useChatPlatformNavigation.setState({target: null});
    onBack();
  }, [brand, queryClient, onBack]);

  const deleteBrandMutation = useMutation({
    mutationFn: async () => {
      await apiRequest('DELETE', `/api/brands/${brandId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      toast({ title: 'Brand deleted' });
      onBack();
    },
  });



  useEffect(() => {
    setVisualIdentityStatus('idle');
  }, [brandId]);

  const handleAnalyzeVisualIdentity = async () => {
    if (visualIdentityStatus === 'loading') return;
    setVisualIdentityStatus('loading');
    try {
      const res = await fetch(`/api/brands/${brandId}/analyze-visual-identity`, { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Analysis failed');
      }
      setVisualIdentityStatus('success');
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId] });
      toast({ title: 'Identity suggestions ready', description: 'Review the proposed values in Overview or Brand Identity. Approved values are unchanged.' });
    } catch (err: any) {
      setVisualIdentityStatus('error');
      toast({ title: 'Analysis Failed', description: err.message || 'Could not analyze visual identity.', variant: 'destructive' });
    }
  };

  const tabs = [
    { key: 'overview' as const, label: 'Overview', icon: Package },
    { key: 'angles' as const, label: 'Ad Angles', icon: Lightbulb },
    { key: 'competitors' as const, label: 'Competitors', icon: Users },
    { key: 'intelligence' as const, label: 'Ad Intelligence', icon: Brain },
    { key: 'identity' as const, label: 'Branding', icon: Sparkles },
  ];

  if (brand === null) return null;
  if (isBrandLoading && !brand) {
    return <DetailSkeleton />;
  }

  const displayBrand = brand ?? initialBrand;

  return (
    <div className="brand-workspace-detail">
      {/* Header */}
      <div className="brand-workspace-detail-header">
        <button
          data-testid="button-canvas-brand-back"
          aria-label="Back to brands"
          onClick={onBack}
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: '#EAF0F7',
            border: '1px solid #EAF0F7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')}
        >
          <ArrowLeft style={{ width: 15, height: 15, color: '#334155' }} />
        </button>

        <BrandLogoControl record={displayBrand} size={52} />

        <div className="brand-workspace-detail-title">
          <h1 data-testid="text-canvas-brand-detail-name" className="brand-detail-title">
            {displayBrand.name}
          </h1>
          <div className="brand-detail-meta" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
            <span style={{ fontSize: 13, color: '#5F6D82' }}>{displayBrand.industry || 'General'}</span>
            <StatusBadge status={displayBrand.status} />
            {displayBrand.website && (
              <a
                className="brand-detail-website"
                href={displayBrand.website.startsWith('http') ? displayBrand.website : `https://${displayBrand.website}`}
                target="_blank"
                rel="noopener noreferrer"
                title={displayBrand.website}
                style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, color: '#526074', textDecoration: 'none' }}
              >
                <Link2 style={{ width: 11, height: 11, flexShrink: 0 }} />
                <span className="brand-detail-website-text">{displayBrand.website}</span>
              </a>
            )}
          </div>
        </div>

        <div className="brand-workspace-detail-actions">
          <button
            data-testid="button-canvas-analyze-visual-identity"
            onClick={handleAnalyzeVisualIdentity}
            disabled={visualIdentityStatus === 'loading'}
            style={{
              padding: '7px 14px',
              borderRadius: 10,
              border: visualIdentityStatus === 'success' ? '1px solid #22c55e' : visualIdentityStatus === 'error' ? '1px solid #f87171' : '1px solid #DCE3ED',
              background: 'white',
              fontSize: 13,
              color: visualIdentityStatus === 'success' ? '#15803d' : visualIdentityStatus === 'error' ? '#dc2626' : '#334155',
              cursor: visualIdentityStatus === 'loading' ? 'not-allowed' : 'pointer',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              opacity: visualIdentityStatus === 'loading' ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (visualIdentityStatus !== 'loading') (e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7';
            }}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'white')}
          >
            {visualIdentityStatus === 'loading' ? (
              <><Loader2 style={{ width: 13, height: 13 }} className="animate-spin" />Analyzing...</>
            ) : visualIdentityStatus === 'success' ? (
              <>Visual Identity Analyzed ✓</>
            ) : visualIdentityStatus === 'error' ? (
              <>Analysis Failed — Retry</>
            ) : (
              <>Analyze Visual Identity</>
            )}
          </button>
          <button
            data-testid="button-canvas-edit-brand"
            onClick={() => setShowEditModal(true)}
            style={{
              padding: '7px 14px',
              borderRadius: 10,
              border: '1px solid #DCE3ED',
              background: 'white',
              fontSize: 13,
              color: '#334155',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'white')}
          >
            Edit
          </button>
          <button
            data-testid="button-canvas-delete-brand"
            aria-label={`Delete ${displayBrand.name}`}
            onClick={() => setShowDeleteModal(true)}
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              border: '1px solid #FCA5A5',
              background: 'white',
              color: '#DC2626',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FFF5F5')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'white')}
          >
            <Trash2 style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="brand-workspace-tabs" style={{ display: 'flex', gap: 2, overflowX: 'auto', overflowY: 'hidden', borderBottom: '1px solid #EAF0F7', marginBottom: 20 }}>
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            data-testid={`tab-canvas-brand-${key}`}
            onClick={() => setTab(key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '9px 14px',
              fontSize: 13.5,
              fontWeight: tab === key ? 600 : 400,
              color: tab === key ? '#1A1A1A' : '#5F6D82',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === key ? '2px solid #1A1A1A' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: -1,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            } as React.CSSProperties}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="brand-content" data-testid="brand-tab-content">
      {tab === 'overview' && <OverviewTab brand={displayBrand} brandId={displayBrand.id} refreshSignal={productsRefresh} startWithProduct={productSetupPending} onProductSetupOpened={() => setProductSetupPending(false)} />}
      {tab === 'angles' && <AdAnglesTab brandId={displayBrand.id} />}
      {tab === 'competitors' && (
        <section className="brand-section" data-testid="brand-competitors-tab">
          <CompetitorAnalysisContent hideHeader brandId={displayBrand.id} />
        </section>
      )}
      {tab === 'intelligence' && <AdIntelligenceTab brandId={displayBrand.id} />}
      {tab === 'identity' && (
        <div style={{ background: '#FFFFFF' }} data-testid="brand-identity-tab">
          <BrandIdentityTab brandId={displayBrand.id} />
        </div>
      )}
      </div>
      {productsEditTarget && (
        <AddProductModal
          brandId={displayBrand.id}
          product={productsEditTarget}
          onClose={() => setProductsEditTarget(null)}
          onSuccess={() => { setProductsEditTarget(null); setProductsRefresh((n) => n + 1); }}
        />
      )}
      {showEditModal && (
        <EditBrandModal brand={displayBrand} onClose={() => setShowEditModal(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/brands'] })} />
      )}
      {showDeleteModal && (
        <DeleteConfirmModal
          title="Delete brand"
          message={`Are you sure you want to delete "${displayBrand.name}" and all its products? This cannot be undone.`}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => deleteBrandMutation.mutate()}
          isLoading={deleteBrandMutation.isPending}
        />
      )}
    </div>
  );
}

export function CanvasBrandsView() {
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [newBrandId, setNewBrandId] = useState<number | null>(null);
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
  const [analyzeModalUrl, setAnalyzeModalUrl] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();

  // Pop the analyzer modal automatically when the chat handed off a URL.
  // Consume + clear the store field on the way through so we don't
  // re-open the modal if the user navigates away and back.
  const pendingUrl = useCanvasStore((s) => s.pendingBrandModalUrl);
  useEffect(() => {
    if (pendingUrl === null) return;
    setAnalyzeModalUrl(pendingUrl || undefined);
    setShowAnalyzeModal(true);
    useCanvasStore.setState({ pendingBrandModalUrl: null });
  }, [pendingUrl]);

  const { data: brands = [], isLoading } = useQuery<Brand[]>({
    queryKey: ['/api/brands'],
  });

  const chatDestination = useChatPlatformNavigation(s => s.target);
  const { toast } = useToast();
  useEffect(() => {
    if (!chatDestination || isLoading) return;
    const brand = brands.find(item => item.id === chatDestination.brandId);
    if (brand) setSelectedBrand(brand);
    else toast({ title: 'Brand unavailable', description: 'This brand could not be opened.', variant: 'destructive' });
    if (!brand || chatDestination.productId == null && chatDestination.competitorId == null) useChatPlatformNavigation.setState({ target: null });
  }, [chatDestination, brands, isLoading, toast]);

  // v7.08 — which brands are mid-scan, for the greyed "Analyzing Brand" tiles.
  // ONE bulk call for the whole grid (never one poll per tile), and it only
  // polls while something is actually in flight. Fail-open: the endpoint
  // returns {} on error, so a hiccup just renders normal tiles.
  const { data: scanning = {} } = useQuery<Record<string, string>>({
    queryKey: ['/api/brands/onboarding/active'],
    refetchInterval: (q) => (Object.keys((q.state.data as Record<string, string>) ?? {}).length > 0 ? 3000 : 8000),
  });

  const handleBrandSelected = (brand: Brand, reused = false) => {
    queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
    setNewBrandId(reused ? null : brand.id);
    setSelectedBrand(brand);
  };

  if (selectedBrand) {
    const initialBrand = brands.find((b) => b.id === selectedBrand.id) || selectedBrand;
    return (
      <div className="brand-detail-page brand-workspace-page">
        <BrandDetailView key={selectedBrand.id} brandId={selectedBrand.id} initialBrand={initialBrand} startWithProduct={newBrandId === selectedBrand.id} requestedCompetitorId={chatDestination?.brandId === selectedBrand.id ? chatDestination.competitorId : undefined} requestedProductId={chatDestination?.brandId === selectedBrand.id ? chatDestination.productId : undefined} onBack={() => { setSelectedBrand(null); setNewBrandId(null); }} />
      </div>
    );
  }

  return (
    <div className="brand-workspace-page">
      {/* Page header */}
      <div className="brand-workspace-header">
        <div>
          <span className="brand-workspace-eyebrow">Your workspace · {brands.length} brand{brands.length !== 1 ? 's' : ''}</span>
          <h1>Brands &amp; products</h1>
          <p>Keep each brand’s identity and products together, ready for your next ad.</p>
        </div>
        <button
          data-testid="button-canvas-analyze-new-brand"
          className="brand-workspace-add-button"
          onClick={() => setShowAnalyzeModal(true)}
        >
          <Plus style={{ width: 15, height: 15 }} />
          Add a brand
        </button>
      </div>

      {isLoading ? (
        <div className="brand-workspace-grid">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : brands.length === 0 ? (
        <AddBrandCard onClick={() => setShowAnalyzeModal(true)} />
      ) : (
        <div className="brand-workspace-grid">
          {brands.map((brand) => (
            <BrandCard
              key={brand.id}
              brand={brand}
              scanning={Boolean(scanning[String(brand.id)])}
              onClick={() => setSelectedBrand(brand)}
            />
          ))}
        </div>
      )}

      {showAnalyzeModal && (
        <AnalyzeBrandModal
          onClose={() => { setShowAnalyzeModal(false); setAnalyzeModalUrl(undefined); }}
          onSuccess={handleBrandSelected}
          initialUrl={analyzeModalUrl}
        />
      )}
    </div>
  );
}
